/** Psychoeducation articles — single source for seed data and UI fallbacks */
/** Card hero images — calm, representative Pexels photography */
export function learnArticlePexelsUrl(photoId, width = 800) {
    return `https://images.pexels.com/photos/${photoId}/pexels-photo-${photoId}.jpeg?auto=compress&cs=tinysrgb&w=${width}&h=440&fit=crop`;
}
export const FEATURED_LEARN_SLUGS = [
    'understanding-anxiety',
    'building-healthy-habits',
    'when-to-seek-therapy',
];
export const LEARN_ARTICLES = [
    {
        slug: 'understanding-anxiety',
        title: 'Understanding anxiety',
        category: 'Mental health',
        imageUrl: learnArticlePexelsUrl(4037987),
        summary: 'What anxiety really is, how it shows up in the body and mind, and practical ways to feel more grounded.',
        expandParagraphs: [
            'Anxiety is your nervous system preparing for a threat — even when no immediate danger is present. A racing heart, tight chest, restlessness, or constant worry are common signs. Occasional anxiety is normal; it becomes worth attention when it lingers, grows stronger, or gets in the way of work, relationships, or sleep.',
            'Helpful first steps include slow breathing (inhale 4 counts, exhale 6), naming what you feel without judging it, and limiting caffeine or late-night scrolling when you are already on edge. Writing down recurring worries can reveal patterns and make them feel less overwhelming.',
            'If anxiety feels unmanageable or you are avoiding life because of it, speaking with a licensed therapist can help. Cognitive behavioural therapy (CBT) and other evidence-based approaches are especially effective for anxiety.',
        ],
        body: `Anxiety is one of the most common mental health experiences worldwide — and in Nigeria, growing economic pressure, traffic, work demands, and family expectations can all contribute to feeling constantly on alert.

What anxiety feels like

Anxiety can be mental (racing thoughts, dread, difficulty concentrating), physical (rapid heartbeat, sweating, stomach upset, muscle tension), or behavioural (avoiding places, people, or tasks). Everyone's pattern is different.

When is it more than everyday stress?

Consider professional support if anxiety:
• Lasts most days for several weeks
• Interferes with sleep, appetite, or daily responsibilities
• Leads you to avoid important activities or relationships
• Comes with panic attacks or feelings of losing control

Evidence-based coping strategies

1. Grounding breath — Breathe in for 4 seconds, hold briefly, breathe out for 6. Repeat for 2–3 minutes.
2. Thought checking — Ask: "What is the evidence for and against this worry? What would I tell a friend?"
3. Movement — Even a 10-minute walk can lower physiological arousal.
4. Routine — Regular sleep and meal times stabilise mood and energy.

Therapy can help

Licensed therapists on Mylighthouse use approaches such as CBT to help you understand triggers, challenge unhelpful thought patterns, and build lasting skills. You are not weak for seeking support — you are taking care of your health.`,
        authorName: 'Mylighthouse Clinical Team',
    },
    {
        slug: 'building-healthy-habits',
        title: 'Building healthy habits',
        category: 'Wellness',
        imageUrl: learnArticlePexelsUrl(1640777),
        summary: 'Small, steady routines for sleep, movement, and mindfulness that support emotional balance over time.',
        expandParagraphs: [
            'Lasting wellness rarely comes from drastic changes. Research shows that tiny, repeatable habits — done consistently — reshape how you feel more reliably than occasional bursts of effort.',
            'Start with one anchor: a fixed wake time, a five-minute morning stretch, or jotting down one thing you are grateful for before bed. Pair new habits with something you already do (e.g. deep breaths while your kettle boils) so they stick more easily.',
            'Be kind when you miss a day. Progress is measured in weeks and months, not perfection. If a habit stops working, adjust it rather than abandoning self-care altogether.',
        ],
        body: `Healthy habits are the foundation of emotional wellness. They do not replace therapy when you need professional support — but they create the conditions for your mind and body to recover and thrive.

Sleep

Aim for a consistent bedtime and wake time, even on weekends. Dim screens an hour before sleep, keep your room cool and dark, and avoid heavy meals late at night. Poor sleep amplifies anxiety and low mood.

Movement

You do not need a gym membership. Walking, dancing, stretching, or household chores all count. Movement releases tension and improves sleep quality. Even 15–20 minutes most days makes a measurable difference.

Mindfulness and pause

Mindfulness is simply paying attention to the present moment without harsh judgment. Try: three slow breaths before opening email, a short body scan at lunch, or two minutes of quiet after Maghrib or evening prayer.

Connection

Humans regulate emotions through relationships. A weekly check-in with a trusted friend, family member, or support group protects against isolation.

Building your plan

1. Choose one habit for this week — only one.
2. Make it specific: "Walk 10 minutes after dinner" beats "exercise more."
3. Track it on paper or your phone — seeing streaks motivates continuity.
4. Review after two weeks and add a second habit if the first feels natural.

Mylighthouse therapists can help you design a realistic wellness plan tailored to your life and culture.`,
        authorName: 'Mylighthouse Wellness Team',
    },
    {
        slug: 'when-to-seek-therapy',
        title: 'When to seek therapy',
        category: 'Getting started',
        imageUrl: learnArticlePexelsUrl(7176327),
        summary: 'Clear signs that professional support could help — and how to take the first step on Mylighthouse with confidence.',
        expandParagraphs: [
            'Therapy is not only for crisis. Many people begin when they feel "off" for weeks, when stress outpaces their usual coping tools, or when they want support through a life change — grief, a new job, relationship strain, or becoming a parent.',
            'Strong signals to reach out include persistent low mood, loss of interest in things you used to enjoy, difficulty functioning at work or home, trauma memories that will not fade, or using alcohol or substances to cope. You do not need to wait until things feel unbearable.',
            'On Mylighthouse, you can browse verified therapists, take a free PHQ-9/GAD-7 assessment, and book video, voice, or chat sessions in naira. Your first step can be as small as reading a profile and sending a message.',
        ],
        body: `Deciding to see a therapist is a sign of strength, not failure. Mental health care works the same way as care for your body — early attention often leads to faster recovery.

You might benefit from therapy if you:

• Feel sad, empty, or hopeless most days for two weeks or more
• Worry excessively or feel on edge without a clear reason
• Have trouble sleeping, eating, or concentrating
• Pull away from friends, family, or activities you used to enjoy
• Are grieving, navigating divorce, or facing major life stress
• Have experienced trauma, abuse, or violence — recent or in the past
• Use alcohol, drugs, or gambling to numb difficult feelings
• Have thoughts of harming yourself — please seek help immediately

What therapy on Mylighthouse looks like

1. Create a free account and complete your profile.
2. Optional: take our clinical assessments (PHQ-9, GAD-7) to understand your needs.
3. Browse therapists verified through CASSON, NACP, and other recognised bodies.
4. Book a session — video, voice, or chat — at a time that suits you.
5. Pay securely in naira; sessions are confidential.

What to expect in your first session

Your therapist will ask about what brought you in, your history, and your goals. You do not need to share everything at once. It is okay to feel nervous. The goal of the first meeting is to build trust and agree on a direction together.

There is no wrong reason to start

Some clients come for diagnosable conditions; others come for stress, relationships, self-esteem, or personal growth. If something is weighing on you, that is reason enough.

Ready to begin? Browse therapists on Mylighthouse or take our free assessment to find a starting point.`,
        authorName: 'Mylighthouse Team',
    },
    {
        slug: 'managing-stress-at-work',
        title: 'Managing stress at work',
        category: 'Wellness',
        imageUrl: learnArticlePexelsUrl(1181639),
        summary: 'Recognise burnout early and set boundaries that protect your mental health on the job.',
        expandParagraphs: [
            'Work stress in fast-paced environments can become chronic when deadlines, commuting, and always-on messaging never let your nervous system rest.',
            'Watch for irritability, exhaustion, cynicism, or declining performance — early burnout signals. Micro-breaks, clear start/stop times, and saying no to non-essential tasks are practical protections.',
        ],
        body: `Work-related stress is one of the most common reasons Nigerians seek therapy. Long commutes, economic uncertainty, and blurred work-life boundaries in remote roles all play a role.

Signs of unhealthy work stress

• Dreading Monday every week
• Checking email late at night from habit, not necessity
• Headaches, fatigue, or frequent illness
• Feeling ineffective despite working longer hours

Practical strategies

Set a shutdown ritual at the end of the workday. Communicate realistic timelines. Delegate where possible. Use annual leave without guilt — rest is productive.

When to get professional help

If stress is affecting sleep, relationships, or physical health for more than a few weeks, a therapist can help you build boundaries and coping skills tailored to your workplace.`,
        authorName: 'Mylighthouse Clinical Team',
    },
    {
        slug: 'supporting-a-loved-one',
        title: 'Supporting a loved one',
        category: 'Relationships',
        imageUrl: learnArticlePexelsUrl(1181717),
        summary: 'How to be there for someone struggling with their mental health — without fixing everything yourself.',
        expandParagraphs: [
            'You do not need the perfect words. Listening without judgment, asking "How can I support you?" and avoiding phrases like "just cheer up" often matter more than advice.',
            'Encourage professional help gently — offer to help them find a therapist or sit with them while they book. Look after your own wellbeing too; supporting someone is easier when you are not depleted.',
        ],
        body: `When someone you care about is struggling, it is natural to feel helpless. Your presence and patience can make a real difference.

Do

• Listen more than you speak
• Validate their feelings ("That sounds really hard")
• Ask what they need instead of assuming
• Suggest Mylighthouse or a GP if symptoms persist
• Check in regularly without pressure

Avoid

• Comparing their pain to others' ("Others have it worse")
• Forcing solutions before they are ready
• Sharing their struggles without permission

Take care of yourself

Caregiver fatigue is real. Consider your own support network or speaking with a therapist if you feel overwhelmed.`,
        authorName: 'Mylighthouse Team',
    },
    {
        slug: 'before-therapy',
        title: 'Before therapy',
        category: 'Therapy guide',
        imageUrl: learnArticlePexelsUrl(4101143),
        summary: 'How to arrive at a first or next session without needing perfect words.',
        expandParagraphs: [
            'A short note about what has been hard is enough. You do not need a diagnosis or a speech.',
            'Pick a quiet place and a time you can keep. Nervousness is normal, and you can set the pace.',
        ],
        body: `You do not need the perfect words. A short note about what has been hard — sleep, work, a relationship, a feeling you cannot name — is enough to start.

Choose a quiet place and a time you can keep. If you are booking video or audio, test your connection. If you prefer chat, you can still take a minute to write what you want from the hour.

It is normal to feel nervous. Your clinician is there to listen, not to judge. You can say what you are comfortable sharing and leave the rest for later.`,
        authorName: 'Mylighthouse Team',
    },
    {
        slug: 'during-therapy',
        title: 'During therapy',
        category: 'Therapy guide',
        imageUrl: learnArticlePexelsUrl(7176026),
        summary: 'What to do in the room, on the call, or in chat.',
        expandParagraphs: [
            'Say what is true today, even if you do not know where to start.',
            'If a suggestion does not fit your life, say so. You can pause.',
        ],
        body: `Say what is true for you today, even if it is “I do not know where to start.” Your clinician will help you find a thread.

Ask questions. If a suggestion does not fit your life, say so. Therapy works better when the plan is one you can actually use between sessions.

If something feels too fast or too personal, you can pause. You are allowed to set the pace.`,
        authorName: 'Mylighthouse Team',
    },
    {
        slug: 'after-therapy',
        title: 'After therapy',
        category: 'Therapy guide',
        imageUrl: learnArticlePexelsUrl(3759657),
        summary: 'How to carry one useful thing out of the session.',
        expandParagraphs: [
            'One small next step is more useful than trying to remember everything.',
            'If you are in crisis, use emergency services. A booked session is not an emergency line.',
        ],
        body: `Give yourself a few quiet minutes before jumping back into messages or traffic. One small thing from the session — a breathing practice, a sentence you want to remember, a next step — is more useful than trying to recall everything.

Journaling the same day helps the work stick. If a feeling lingers, you can bring it to the next session.

If you are in crisis, use the crisis line on this site or local emergency services. A therapy appointment is not an emergency service.`,
        authorName: 'Mylighthouse Team',
    },
];
const LEARN_ARTICLE_IMAGE_BY_SLUG = Object.fromEntries(LEARN_ARTICLES.map((article) => [article.slug, article.imageUrl]));
export function getLearnArticleBySlug(slug) {
    return LEARN_ARTICLES.find((a) => a.slug === slug);
}
/** Prefer CMS imageUrl; fall back to curated defaults for known slugs */
export function resolveLearnArticleImageUrl(slug, imageUrl) {
    const trimmed = imageUrl?.trim();
    if (trimmed)
        return trimmed;
    return LEARN_ARTICLE_IMAGE_BY_SLUG[slug] ?? null;
}
export function getFeaturedLearnArticles() {
    return FEATURED_LEARN_SLUGS.map((slug) => LEARN_ARTICLES.find((a) => a.slug === slug)).filter(Boolean);
}
export function formatArticleDate(publishedAt) {
    if (!publishedAt)
        return null;
    return new Date(publishedAt).toLocaleDateString('en-NG', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
export function estimateArticleReadMinutes(parts) {
    const text = [parts.summary, ...(parts.expandParagraphs ?? []), parts.body ?? '']
        .join(' ')
        .trim();
    const words = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
}
//# sourceMappingURL=learnArticles.js.map